import React, { useState } from 'react';
import { Link } from 'react-router-dom';

import { useAuth } from '../../hooks/useAuth';

interface SignInFormProps {
  onSuccess?: () => void;
  onCancel?: () => void;
}

const SignInForm: React.FC<SignInFormProps> = ({ onSuccess, onCancel }) => {
  const { signIn, error } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Form validation
    if (!email.trim()) {
      setFormError('Email is required');
      return;
    }

    if (!password) {
      setFormError('Password is required');
      return;
    }

    setFormError(null);
    setLoading(true);

    try {
      await signIn(email, password);
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      // Error is handled by the auth context
      console.error('Sign in error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full max-w-md mx-auto">
      <h2 className="text-2xl font-bold text-center mb-6 text-dark">Sign In</h2>

      {/* Error message */}
      {(formError || error) && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {formError || error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Email field */}
        <div>
          <label htmlFor="email" className="block text-gray-700 font-medium mb-2">
            Email
          </label>
          <input
            type="email"
            id="email"
            className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="Enter your email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            disabled={loading}
          />
        </div>

        {/* Password field */}
        <div>
          <label htmlFor="password" className="block text-gray-700 font-medium mb-2">
            Password
          </label>
          <input
            type="password"
            id="password"
            className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="Enter your password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            disabled={loading}
          />
        </div>

        {/* Forgot password link */}
        <div className="text-right">
          <Link to="/reset-password" className="text-primary hover:text-primary-dark text-sm">
            Forgot password?
          </Link>
        </div>

        {/* Submit button */}
        <button
          type="submit"
          className={`w-full py-2 px-4 ${
            loading ? 'bg-gray-400' : 'bg-primary hover:bg-primary-dark'
          } text-white rounded-lg transition-colors`}
          disabled={loading}
        >
          {loading ? (
            <>
              <span className="inline-block animate-spin mr-2">↻</span>
              Signing in...
            </>
          ) : (
            'Sign In'
          )}
        </button>

        {/* Sign up link */}
        <div className="text-center mt-4">
          <span className="text-gray-600">Don&apos;t have an account? </span>
          <Link to="/sign-up" className="text-primary hover:text-primary-dark">
            Sign Up
          </Link>
        </div>

        {/* Cancel button (for modal use) */}
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="w-full py-2 px-4 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors mt-2"
            disabled={loading}
          >
            Cancel
          </button>
        )}
      </form>
    </div>
  );
};

export default SignInForm;
